/**
 * The provenance and people on an RCA ticket row (TRUS-687): where the ticket came
 * from (origin badge) and, compactly, who reported it and who it's assigned to.
 * Renders nothing for a row from an older backend that doesn't send these fields.
 */
import { type CSSProperties } from 'react';
import { tvTokens } from '../../../shared/components/triveny';
import { ORIGIN_LABEL, ORIGIN_SHORT, WAITING_ON_LABEL, personLabel, type RcaPerson, type WaitingOn } from './rcaFilterModel';

export function RcaOriginBadge({ id, origin }: { id: string; origin?: string | null }) {
  if (!origin) return null;
  return (
    <span
      data-testid={`rca-origin-${id}`}
      title={`Origin: ${ORIGIN_LABEL[origin] ?? origin}`}
      style={{
        fontFamily: tvTokens.fontMono,
        fontSize: 9.5,
        fontWeight: 600,
        padding: '1px 6px',
        borderRadius: 5,
        border: `1px solid ${tvTokens.border}`,
        color: tvTokens.ink500,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
      }}
    >
      {ORIGIN_SHORT[origin] ?? ORIGIN_LABEL[origin] ?? origin}
    </span>
  );
}

/**
 * The people/meta line under a ticket's title (TRUS-717): the creator/reporter, the
 * organization ("No organization" when none), and who it is waiting on — each spelled out
 * with a label rather than the old "by X · → Y". The Jira assignee is deliberately NOT shown:
 * every ticket is assigned to Vineet, so "Waiting on" (from the hand-off stage) is the useful
 * signal instead. `waitingOn` is derived by the caller via waitingOn(t).
 */
export function RcaPeopleLine({
  id,
  reporter,
  organization,
  waitingOn,
}: {
  id: string;
  reporter?: RcaPerson | null;
  organization?: string | null;
  waitingOn?: WaitingOn | null;
}) {
  const creator = personLabel(reporter);
  const org = (organization || '').trim();
  const wait = waitingOn ? WAITING_ON_LABEL[waitingOn] : '';
  const field: CSSProperties = { whiteSpace: 'nowrap' };
  const key: CSSProperties = { color: tvTokens.ink500, fontWeight: 600 };
  return (
    <span
      data-testid={`rca-people-${id}`}
      style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '0 8px', fontSize: 10.5, color: tvTokens.ink400, minWidth: 0, maxWidth: '100%', lineHeight: 1.35 }}
    >
      <span style={field} title={reporter?.email ? `Creator: ${creator} (${reporter.email})` : creator ? `Creator: ${creator}` : 'No creator recorded'}>
        <span style={key}>Creator:</span> {creator || '—'}
      </span>
      <span style={field} title={org ? `Organization: ${org}` : 'No organization'}>
        <span style={key}>Org:</span> {org || 'No organization'}
      </span>
      {wait && (
        <span style={field} title={`Waiting on: ${wait}`}>
          <span style={key}>Waiting on:</span> {wait}
        </span>
      )}
    </span>
  );
}
